import { X } from 'lucide-react'
import type { ReactNode } from 'react'
import { useCallback, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

import { useT } from '@/i18n'
import { cn } from '@/lib/cn'

interface DialogProps {
  open: boolean
  onClose: () => void
  title?: string
  children: ReactNode
  className?: string
}

/** 轻量模态框：Esc 关闭、点遮罩关闭、打开时锁滚动、焦点进入对话框。 */
export function Dialog({ open, onClose, title, children, className }: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const t = useT()

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    },
    [onClose],
  )

  useEffect(() => {
    if (!open) return
    document.addEventListener('keydown', handleKeyDown)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    panelRef.current?.focus()
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = previousOverflow
    }
  }, [open, handleKeyDown])

  if (!open || typeof document === 'undefined') return null

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-scrim/40 p-0 sm:p-6 sm:pt-14"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={cn(
          'flex max-h-full w-full flex-col overflow-hidden bg-surface outline-none',
          'sm:max-w-[600px] sm:rounded-md sm:shadow-xl',
          'h-full sm:h-auto',
          className,
        )}
      >
        <div className="flex items-center gap-4 px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            aria-label={t('ui.close')}
            className="rounded-md p-2 hover:bg-surface-hover"
          >
            <X className="size-5" />
          </button>
          {title && <h2 className="text-lg font-bold">{title}</h2>}
        </div>
        <div className="flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>,
    document.body,
  )
}
